import type { Identifier } from "ra-core";

import {
  classifyProspectAge,
  getElapsedHours,
  selectProspectInboxDeals,
} from "../deals/prospectInbox";
import {
  countPosiAnswers,
  isPosiComplete,
  type PosiAnswers,
} from "../deals/posi";
import {
  compareLocalDates,
  getLocalDateParts,
  localDateFromInstant,
  MEXICO_CITY_TIME_ZONE,
  type LocalDate,
} from "../incentives/periods";
import type { Client, Contact, Deal, Task } from "../types";
import { computeUpcomingBirthdays } from "./upcomingBirthdaysWindow";

/**
 * Almikia Epic E7, TASK-089: the pure (no fetching, no i18n) logic behind
 * the asesora's "Hoy" home -- the "Tu plan de hoy" ring and the "Quién te
 * necesita hoy" person-card list from `docs/almikia/design/A-movil`. Kept
 * separate from `AdvisorToday.tsx` so the urgency ordering and the touch
 * count are unit-testable without rendering anything.
 */

const DISCOVERY_STAGE = "discovery";

/** The 4 POSI fields, in the canonical Perfil/Objetivos/eStrategias/Issues order. */
const POSI_FIELD_ORDER = [
  "posi_intent",
  "posi_budget",
  "posi_payment_method",
  "posi_decision_maker",
] as const;
export type PosiField = (typeof POSI_FIELD_ORDER)[number];

const isBlankPosiAnswer = (value: string | null | undefined): boolean =>
  value == null || value.trim() === "";

/** The first (canonical-order) POSI field still unanswered, if any. */
function firstMissingPosiField(
  answers: Partial<PosiAnswers>,
): PosiField | undefined {
  return POSI_FIELD_ORDER.find((field) => isBlankPosiAnswer(answers[field]));
}

/** The reason a card is shown -- drives both its "why" copy and its urgency bucket. */
export type HoyReason =
  | { kind: "prospect"; flag: "overdue" | "due_soon"; hours: number }
  /**
   * `activityName` is set for a catalogue next-step task (Almikia Epic EA,
   * TASK-112: `deal_id` and `activity_type_id` both set) and undefined for a
   * manual task, which keeps the plain "Tarea vencida"/"Tarea de hoy" copy.
   */
  | { kind: "task_overdue"; activityName?: string }
  | { kind: "task_today"; activityName?: string }
  | { kind: "birthday"; when: "today" | "tomorrow" }
  | { kind: "posi_incomplete"; answered: number; missingField: PosiField };

/**
 * The two reason kinds that count as a "touch" for the ring (a Prospecto
 * lead about to breach its SLA, or a task due today or earlier) --
 * deliberately NOT the birthday and POSI reasons, which are commercial
 * opportunities/signals, not scheduled touches (DOMAIN RULE (k):
 * `classifyProspectAge` is a service-SLA signal, never turned into a
 * per-advisor count beyond exactly this ring).
 */
const TOUCH_REASON_KINDS = new Set<HoyReason["kind"]>([
  "prospect",
  "task_overdue",
  "task_today",
]);

export type HoyCard = {
  /** Stable across renders: `${reason.kind}-${sourceId}`. */
  id: string;
  reason: HoyReason;
  name: string;
  initials: string;
  /** Raw phone digits/string as stored; null when no phone is on file. */
  phone: string | null;
  /**
   * Set when the card is lead-backed (Prospecto, POSI incompleto, or a
   * catalogue next-step task): desktop navigates to `/deals/:id/show` and
   * the card offers "Registrar actividad" (Almikia Epic EA, TASK-112).
   * Unset for a manual task or a birthday, which offer neither.
   */
  dealId?: Identifier;
  /**
   * The contact to navigate to on mobile (no lead screen exists yet), and
   * on desktop for a card with no lead of its own (a task or a birthday).
   */
  contactId?: Identifier | null;
  /** Birthday cards have no lead or task backing them; desktop uses this. */
  clientId?: Identifier;
  /**
   * Almikia Epic EW1 (TASK-125): the resolved contact backing this card
   * (structurally a `WhatsAppActionContact` -- `id`/`first_name`/
   * `phone_jsonb`), so `HoyPersonCard.tsx` can place the shared "Enviar
   * WhatsApp" action without a second lookup. Null when `contactId` didn't
   * resolve to an already-fetched contact (no phone data to prefill from).
   */
  contact: HoyContact | null;
};

/**
 * Almikia Epic ER (TASK-007): exported so `alerts/useLeadAlerts.ts` builds
 * its own cards (name, initials, phone) the exact same way, without
 * duplicating this logic.
 */
export const initialsFromName = (name: string): string => {
  const [first, second] = name.trim().split(/\s+/).filter(Boolean);
  return `${first?.[0] ?? ""}${second?.[0] ?? ""}`.toUpperCase();
};

/** The first word of a display name -- used as a birthday card's WhatsApp first name when no linked contact resolved. */
const firstWord = (name: string): string => name.trim().split(/\s+/)[0] ?? "";

export const firstPhoneNumber = (
  contact: HoyContact | undefined,
): string | null => contact?.phone_jsonb?.[0]?.number ?? null;

export const contactFullName = (contact: HoyContact | undefined): string =>
  contact ? `${contact.first_name} ${contact.last_name}`.trim() : "";

export type HoyContact = Pick<
  Contact,
  "id" | "first_name" | "last_name" | "phone_jsonb"
>;
export type HoyDeal = Pick<
  Deal,
  | "id"
  | "stage"
  | "created_at"
  | "contact_ids"
  | "archived_at"
  | "posi_intent"
  | "posi_budget"
  | "posi_payment_method"
  | "posi_decision_maker"
>;
export type HoyTask = Pick<
  Task,
  | "id"
  | "contact_id"
  | "due_date"
  | "done_date"
  | "deal_id"
  | "activity_type_id"
>;
export type HoyClient = Pick<
  Client,
  "id" | "full_legal_name" | "birthday" | "phone" | "contact_id"
>;

export type HoyPlan = {
  /** Ordered exactly per the ticket: prospect overdue, due_soon, task overdue, task today, birthdays, POSI. */
  cards: HoyCard[];
  /** Her tasks with `done_date` today (local, America/Mexico_City). */
  doneTodayCount: number;
  /** The touch-type cards among `cards` (see {@link TOUCH_REASON_KINDS}). */
  pendingTouchCount: number;
  /** The single most urgent touch card, if any -- drives the ring's sentence. */
  mostUrgentTouch: HoyCard | null;
};

/** True when `isoString`'s Mexico City calendar date equals `today`. */
const isSameLocalDay = (
  isoString: string,
  today: LocalDate,
  timeZone: string,
): boolean =>
  compareLocalDates(localDateFromInstant(isoString, timeZone), today) === 0;

/** True when `isoString`'s Mexico City calendar date is strictly before `today`. */
const isBeforeLocalDay = (
  isoString: string,
  today: LocalDate,
  timeZone: string,
): boolean =>
  compareLocalDates(localDateFromInstant(isoString, timeZone), today) < 0;

/**
 * Builds the ordered "Quién te necesita hoy" card list plus the ring's
 * input numbers, ASSUMPTION (ticket, corrected after review): a pending
 * touch is exactly a due-soon/overdue Prospecto lead or an undone task due
 * today or earlier -- the same set of items rendered as touch cards, so the
 * ring's "Faltan N toques" can never disagree with an empty card list
 * (fixed regression from the first attempt at this ticket, where the ring
 * also counted on-time Prospecto leads that the card list excludes).
 *
 * ASSUMPTION: a card with no lead of its own (a task or a birthday) opens
 * the same contact show route on both shells -- only a lead-backed card
 * (Prospecto, POSI) has a distinct desktop target (`/deals/:id/show`),
 * since the mobile shell has no lead screen yet.
 */
export function buildHoyPlan(input: {
  deals: HoyDeal[];
  tasks: HoyTask[];
  clients: HoyClient[];
  contactsById: Map<Identifier, HoyContact>;
  /** Catalogue activity name by id, for a next-step task's reason line (NFR: one batched read, no per-card request). */
  activityTypeNamesById?: Map<Identifier, string>;
  now?: Date;
  timeZone?: string;
}): HoyPlan {
  const now = input.now ?? new Date();
  const timeZone = input.timeZone ?? MEXICO_CITY_TIME_ZONE;
  const today = getLocalDateParts(now, timeZone);

  const openDeals = input.deals.filter((deal) => !deal.archived_at);
  const activityTypeNamesById = input.activityTypeNamesById ?? new Map();

  // Prospecto (overdue, then due_soon) -- oldest-first within each bucket,
  // matching `selectProspectInboxDeals`'s own ordering.
  const inboxDeals = selectProspectInboxDeals(openDeals as Deal[]);
  const overdueProspectCards: HoyCard[] = [];
  const dueSoonProspectCards: HoyCard[] = [];
  for (const deal of inboxDeals) {
    const flag = classifyProspectAge(deal.created_at, now);
    if (flag === "on_time") continue;
    const contact = input.contactsById.get(deal.contact_ids?.[0]);
    const card: HoyCard = {
      id: `prospect-${deal.id}`,
      reason: {
        kind: "prospect",
        flag,
        hours: getElapsedHours(deal.created_at, now),
      },
      name: contactFullName(contact),
      initials: initialsFromName(contactFullName(contact)),
      phone: firstPhoneNumber(contact),
      dealId: deal.id,
      contactId: deal.contact_ids?.[0] ?? null,
      contact: contact ?? null,
    };
    (flag === "overdue" ? overdueProspectCards : dueSoonProspectCards).push(
      card,
    );
  }

  // Her tasks, not done, due today or earlier.
  const taskOverdueCards: HoyCard[] = [];
  const taskTodayCards: HoyCard[] = [];
  let doneTodayCount = 0;
  const sortedTasks = [...input.tasks].sort(
    (a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime(),
  );
  for (const task of sortedTasks) {
    if (task.done_date) {
      if (isSameLocalDay(task.done_date, today, timeZone)) doneTodayCount += 1;
      continue;
    }
    const contact = input.contactsById.get(task.contact_id);
    // A catalogue next-step task (Almikia Epic EA, TASK-112): both `deal_id`
    // and `activity_type_id` set. A manual task keeps `dealId` unset (no
    // "Registrar actividad" action, no lead to open) and the generic reason.
    const isNextStepTask =
      task.deal_id != null && task.activity_type_id != null;
    const activityName = isNextStepTask
      ? activityTypeNamesById.get(task.activity_type_id as Identifier)
      : undefined;
    const card: HoyCard = {
      id: `task-${task.id}`,
      reason: isSameLocalDay(task.due_date, today, timeZone)
        ? { kind: "task_today", activityName }
        : { kind: "task_overdue", activityName },
      name: contactFullName(contact),
      initials: initialsFromName(contactFullName(contact)),
      phone: firstPhoneNumber(contact),
      contactId: task.contact_id,
      dealId: isNextStepTask ? (task.deal_id as Identifier) : undefined,
      contact: contact ?? null,
    };
    if (isBeforeLocalDay(task.due_date, today, timeZone)) {
      taskOverdueCards.push(card);
    } else if (isSameLocalDay(task.due_date, today, timeZone)) {
      taskTodayCards.push(card);
    }
  }

  // Clients with a birthday today or tomorrow (reuses the shared 30-day
  // window computation, filtered down to its first 2 days).
  const birthdayRows = computeUpcomingBirthdays(input.clients, now).filter(
    (row) => row.daysUntil <= 1,
  );
  const clientsById = new Map(
    input.clients.map((client) => [client.id, client]),
  );
  const birthdayCards: HoyCard[] = birthdayRows.map((row) => {
    const client = clientsById.get(row.id);
    const linkedContact =
      client?.contact_id != null
        ? (input.contactsById.get(client.contact_id) ?? null)
        : null;
    // Almikia Epic EW1 (TASK-125, final retry round): the synthesized
    // WhatsApp contact is only built when the client has an actual linked
    // contact (`client.contact_id != null`) -- its `id` MUST be the real
    // contact id, never the client's own id. Birthday cards pass no
    // `dealId`, so `WhatsAppAction` falls back to a lead lookup keyed on
    // this id (`contact_ids@cs {<id>}`); reusing the client id there could
    // match an unrelated contact sharing that numeric id and prefill/log
    // against a stranger's lead. A client with no linked contact gets
    // `contact: null` instead, so `HoyPersonCard`'s plain `wa.me` fallback
    // link (no lead lookup, no logging) handles it off `client.phone` alone.
    const contact: HoyContact | null =
      client?.contact_id != null && client.phone
        ? {
            id: client.contact_id,
            first_name:
              linkedContact?.first_name ?? firstWord(row.full_legal_name),
            last_name: linkedContact?.last_name ?? "",
            phone_jsonb: [{ number: client.phone, type: "Other" }],
          }
        : null;
    return {
      id: `birthday-${row.id}`,
      reason: {
        kind: "birthday",
        when: row.daysUntil === 0 ? "today" : "tomorrow",
      },
      name: row.full_legal_name,
      initials: initialsFromName(row.full_legal_name),
      phone: client?.phone ?? null,
      clientId: row.id,
      contactId: client?.contact_id ?? null,
      contact,
    };
  });

  // Discovery leads with an incomplete POSI, oldest first.
  const posiCards: HoyCard[] = openDeals
    .filter(
      (deal) =>
        deal.stage === DISCOVERY_STAGE &&
        !isPosiComplete({
          posi_intent: deal.posi_intent,
          posi_budget: deal.posi_budget,
          posi_payment_method: deal.posi_payment_method,
          posi_decision_maker: deal.posi_decision_maker,
        }),
    )
    .slice()
    .sort(
      (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    )
    .map((deal) => {
      const answers: Partial<PosiAnswers> = {
        posi_intent: deal.posi_intent,
        posi_budget: deal.posi_budget,
        posi_payment_method: deal.posi_payment_method,
        posi_decision_maker: deal.posi_decision_maker,
      };
      const missingField =
        firstMissingPosiField(answers) ?? "posi_decision_maker";
      const answered = countPosiAnswers(answers);
      const contact = input.contactsById.get(deal.contact_ids?.[0]);
      return {
        id: `posi-${deal.id}`,
        reason: { kind: "posi_incomplete", answered, missingField },
        name: contactFullName(contact),
        initials: initialsFromName(contactFullName(contact)),
        phone: firstPhoneNumber(contact),
        dealId: deal.id,
        contactId: deal.contact_ids?.[0] ?? null,
        contact: contact ?? null,
      } satisfies HoyCard;
    });

  const cards = [
    ...overdueProspectCards,
    ...dueSoonProspectCards,
    ...taskOverdueCards,
    ...taskTodayCards,
    ...birthdayCards,
    ...posiCards,
  ];

  const pendingTouchCount = cards.filter((card) =>
    TOUCH_REASON_KINDS.has(card.reason.kind),
  ).length;
  const mostUrgentTouch =
    cards.find((card) => TOUCH_REASON_KINDS.has(card.reason.kind)) ?? null;

  return { cards, doneTodayCount, pendingTouchCount, mostUrgentTouch };
}

/**
 * Almikia Epic ER (TASK-007), acceptance criterion 2: the lead ids already
 * shown as a "Quién te necesita hoy" card -- `alerts/LeadAlertCards.tsx`
 * excludes these so a lead never appears twice (once here, once as a
 * separate alert card); her alert still reaches her, folded into whichever
 * card already names that lead.
 */
export function dealIdsInHoyPlan(plan: HoyPlan): Set<Identifier> {
  const ids = new Set<Identifier>();
  for (const card of plan.cards) {
    if (card.dealId != null) ids.add(card.dealId);
  }
  return ids;
}

/** Digits-only phone, with a `52` (Mexico) prefix added to a bare 10-digit number (ASSUMPTION). */
export function toWhatsAppDigits(rawPhone: string): string {
  const digits = rawPhone.replace(/\D/g, "");
  return digits.length === 10 ? `52${digits}` : digits;
}

/**
 * `https://wa.me/<digits>?text=<encoded>` link, ready before the tap (no
 * network wait -- mobile browsers block a window opened after an await).
 * Almikia Epic EW1 (TASK-124): `message` is the shared action's prefilled
 * text; omitted/empty keeps the original bare-link behavior (no `?text=`),
 * so every pre-EW1 caller (e.g. the Hoy card, until TASK-125 switches it
 * over) is unaffected.
 */
export function buildWhatsAppLink(rawPhone: string, message?: string): string {
  const link = `https://wa.me/${toWhatsAppDigits(rawPhone)}`;
  return message ? `${link}?text=${encodeURIComponent(message)}` : link;
}

/** `tel:` link -- whitespace stripped, everything else kept as stored. */
export function buildTelLink(rawPhone: string): string {
  return `tel:${rawPhone.replace(/\s+/g, "")}`;
}
